import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, Suspense } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Skeleton, Stack } from '@mui/material';
import Cookies from 'js-cookie';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { useApplicationsList } from '../components/ApplicationContext';
import { AuthorizationAPI } from '../apis';
import RemoteAppWrapper from '../components/RemoteAppWrapper';
import { ErrorPage } from '../components/ErrorBoundary';
// Tracks the URL registered for each scope so cleanup knows which script to remove.
const loadedScopeUrls = new Map();
// Exported for test instrumentation only — allows tests to pre-populate scope state.
export const _testLoadedScopeUrls = loadedScopeUrls;
// Removes every loaded bundle so navigating to /apps always starts clean.
export const cleanupAllScopes = () => {
    for (const scope of loadedScopeUrls.keys()) {
        cleanupScope(scope);
    }
};
// Removes a scope's <script> tag and webpack container so the next click
// always fetches a completely fresh bundle.
const cleanupScope = (scope) => {
    const url = loadedScopeUrls.get(scope);
    if (!url)
        return;
    for (const script of document.querySelectorAll('script')) {
        if (script.src === url)
            script.remove();
    }
    loadedScopeUrls.delete(scope);
    // Webpack module federation may register the container with configurable:false,
    // making delete throw in strict mode. Fall back to undefined.
    try {
        delete globalThis[scope];
    }
    catch {
        ;
        globalThis[scope] = undefined;
    }
    // When remoteEntry.js runs its IIFE it reads self["webpackChunk<uniqueName>"] and
    // calls forEach on any entries already there, marking those chunks as installed
    // without a network request. The array name comes from the remote app's
    // output.uniqueName (its package.json name), NOT the scope — so we clear every
    // webpackChunk* array to ensure the new IIFE starts with an empty registry.
    const g = globalThis;
    for (const key of Object.keys(g)) {
        if (key.startsWith('webpackChunk') && Array.isArray(g[key])) {
            try {
                delete g[key];
            }
            catch {
                g[key] = undefined;
            }
        }
    }
};
const createAndLoadScript = (url, scope, resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
        setTimeout(() => handleScriptTimeout(resolve, scope), 500);
    };
    script.onerror = (error) => {
        console.error(`❌ Failed to load remote entry:`, error);
        reject(new Error(`Failed to load ${url}`));
    };
    document.head.appendChild(script);
};
const loadRemoteEntry = (url, scope) => {
    return new Promise((resolve, reject) => {
        try {
            // Full cleanup: removes old script, clears the container, and clears all
            // webpackChunk install arrays so the new IIFE re-fetches bundle chunks.
            // cleanupScope is a no-op when no URL is tracked (first load).
            cleanupScope(scope);
            // Always fetch fresh — timestamp busts the browser cache every time.
            const freshUrl = `${url}?t=${Date.now()}`;
            loadedScopeUrls.set(scope, freshUrl);
            // If the container is already registered (e.g., pre-set in tests to mock
            // the remote entry script), resolve immediately without appending a script.
            // In production this path is never taken: cleanupScope always deletes the
            // container when switching deployments, and it is never set before the first
            // load. The guard exists purely to keep tests fast and deterministic.
            if (globalThis[scope]) {
                return resolve();
            }
            createAndLoadScript(freshUrl, scope, resolve, reject);
        }
        catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
        }
    });
};
const handleScriptTimeout = (resolve, scope) => {
    if (globalThis[scope]) {
        resolve();
    }
    else {
        const availableContainers = Object.keys(globalThis).filter((k) => k.toLowerCase().includes('app') ||
            k.toLowerCase().includes('remote') ||
            k.includes('Remote') ||
            k.includes('App'));
        console.error(`Container ${scope} not found. Available: ${availableContainers.join(', ')}`);
        resolve(); // Continue anyway - let next step handle error
    }
};
const loadRemoteComponent = async (scope, module, entryUrl) => {
    await loadRemoteEntry(entryUrl, scope);
    // Ensure the share scope is initialized. This is a no-op if already done.
    // Do NOT delete __webpack_share_scopes__.default before calling this —
    // webpack's per-module registration flags are set after the first init and
    // won't re-run, leaving the scope empty and breaking container.init().
    // @ts-expect-error - webpack module federation global
    await __webpack_init_sharing__('default');
    const container = globalThis[scope];
    if (!container) {
        const availableContainers = Object.keys(globalThis).filter((k) => k.includes('App') ||
            k.includes('app') ||
            k.includes('Remote') ||
            k.includes('remote'));
        console.error(`Remote container ${scope} not found. Available: ${availableContainers.join(', ')}`);
        throw new Error(`Remote container ${scope} not found. Available: ${availableContainers.join(', ')}`);
    }
    // @ts-expect-error - webpack module federation container
    await container.init(__webpack_share_scopes__.default);
    const factory = await container.get(module);
    const Module = factory();
    return Module;
};
/**
 * Creates a React wrapper for non-React frameworks (Angular, Vue, etc.)
 * that use a mount/unmount pattern
 */
const createFrameworkWrapper = (mountFn, unmountFn) => {
    return function FrameworkWrapper(props) {
        const containerRef = React.useRef(null);
        const instanceRef = React.useRef(null);
        const mountId = React.useId().replaceAll(':', '');
        React.useEffect(() => {
            if (!containerRef.current)
                return;
            // Create mount container
            const mountContainer = document.createElement('div');
            mountContainer.id = `remote-mount-${mountId}`;
            containerRef.current.appendChild(mountContainer);
            // Mount the framework app
            try {
                instanceRef.current = mountFn(mountContainer.id, props);
            }
            catch (error) {
                console.error('Error mounting remote app:', error);
            }
            // Cleanup on unmount
            return () => {
                if (instanceRef.current && unmountFn) {
                    try {
                        unmountFn(instanceRef.current);
                    }
                    catch (error) {
                        console.error('Error unmounting remote app:', error);
                    }
                }
                if (mountContainer.parentNode) {
                    mountContainer.remove();
                }
            };
        }, []);
        return React.createElement('div', {
            ref: containerRef,
            style: { width: '100%', height: '100%' }
        });
    };
};
// Helper function to load remote component (extracted to reduce nesting)
const loadComponentAsync = async (scope, product, entryUrl, setRemoteComponent) => {
    try {
        const mod = await loadRemoteComponent(scope, './app', entryUrl);
        // Handle different export patterns
        let Component = mod.default || mod;
        // Case 1: Already a React component
        if (typeof Component === 'function') {
            // Check if it looks like a React component
            const isReactComponent = Component.prototype?.isReactComponent || // Class component
                Component.toString().includes('React.createElement') || // Likely React function
                Component.$$typeof; // React element type
            if (isReactComponent || Component.length <= 1) {
                // Accept it as a React component
                setRemoteComponent(React.lazy(() => Promise.resolve({ default: Component })));
                return;
            }
        }
        // Case 2: Object with mount/unmount methods (Angular, Vue pattern)
        if (Component && typeof Component === 'object') {
            if (typeof Component.mount === 'function') {
                const WrappedComponent = createFrameworkWrapper(Component.mount.bind(Component), Component.unmount?.bind(Component));
                setRemoteComponent(React.lazy(() => Promise.resolve({ default: WrappedComponent })));
                return;
            }
        }
        // Case 3: Function that returns mount/unmount object
        if (typeof Component === 'function') {
            try {
                const instance = Component();
                if (instance && typeof instance.mount === 'function') {
                    const WrappedComponent = createFrameworkWrapper(instance.mount.bind(instance), instance.unmount?.bind(instance));
                    setRemoteComponent(React.lazy(() => Promise.resolve({ default: WrappedComponent })));
                    return;
                }
            }
            catch {
                // Not a factory function, continue to error
            }
        }
        throw new Error(`Invalid export format in ${scope}. Expected:\n` +
            `- React component (function/class)\n` +
            `- Object with mount() method\n` +
            `- Function returning {mount, unmount}\n` +
            `Got: ${typeof Component}`);
    }
    catch (err) {
        console.error('Error loading component for %s:', scope, err);
        throw err;
    }
};
// Skeleton loader component (extracted from parent component)
const SkeletonLoader = () => (_jsx(Box, { sx: { p: 3, width: '100%' }, "data-testid": "skeleton-loader", children: _jsxs(Stack, { spacing: 2, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3
                }, children: [_jsx(Skeleton, { variant: "circular", width: 40, height: 40 }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Skeleton, { variant: "text", width: "30%", height: 32 }), _jsx(Skeleton, { variant: "text", width: "15%", height: 20 })] })] }), _jsxs(Box, { sx: { mb: 4 }, children: [_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 200, sx: { borderRadius: 2, mb: 2 } }), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Skeleton, { variant: "rectangular", width: "48%", height: 120, sx: { borderRadius: 1 } }), _jsx(Skeleton, { variant: "rectangular", width: "48%", height: 120, sx: { borderRadius: 1 } })] })] }), _jsxs(Box, { children: [_jsx(Skeleton, { variant: "text", width: "20%", height: 28, sx: { mb: 2 } }), [
                        'skeleton-1',
                        'skeleton-2',
                        'skeleton-3',
                        'skeleton-4',
                        'skeleton-5'
                    ].map((id) => (_jsxs(Box, { sx: { display: 'flex', gap: 2, mb: 1 }, children: [_jsx(Skeleton, { variant: "rectangular", width: "15%", height: 24 }), _jsx(Skeleton, { variant: "rectangular", width: "25%", height: 24 }), _jsx(Skeleton, { variant: "rectangular", width: "20%", height: 24 }), _jsx(Skeleton, { variant: "rectangular", width: "15%", height: 24 }), _jsx(Skeleton, { variant: "rectangular", width: "10%", height: 24 })] }, id)))] })] }) }));
const Product = () => {
    const { product } = useParams();
    const location = useLocation();
    const selectedProductId = new URLSearchParams(location.search).get('id') ?? undefined;
    const [remoteComponent, setRemoteComponent] = useState(null);
    const [applicationsList, setApplicationsList] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const getApplications = useApplicationsList();
    useEffect(() => {
        const loadApplications = async () => {
            try {
                const apps = await getApplications();
                setApplicationsList(apps);
            }
            catch (error) {
                console.error('Error loading applications:', error);
            }
        };
        loadApplications();
    }, [getApplications]);
    useEffect(() => {
        let cancelled = false;
        setRemoteComponent(null);
        setErrorMessage(null);
        // Wait for applications list to be loaded
        if (!applicationsList || applicationsList.length === 0) {
            return;
        }
        const getBaseUrlForProduct = (routeProduct) => {
            if (!routeProduct)
                return null;
            try {
                if (!Array.isArray(applicationsList)) {
                    console.error('Unexpected applications list:', applicationsList);
                    return null;
                }
                const products = applicationsList;
                // Match primarily on details.productname, fallback to top-level name
                // Use case-insensitive matching for flexibility
                const routeProductLower = routeProduct.toLowerCase();
                const match = products.find((p) => p?.details?.productName?.toLowerCase() ===
                    routeProductLower ||
                    p?.name?.toLowerCase() === routeProductLower);
                if (!match) {
                    console.error(`No application found: "${routeProduct}"`);
                    return null;
                }
                const baseUrl = match?.details?.productUrl;
                return baseUrl ?? null;
            }
            catch (e) {
                console.error('Failed to get base URL:', e);
                return null;
            }
        };
        const load = async () => {
            const routeProductLower = product?.toLowerCase() ?? '';
            // Prefer matching by ID from the URL (?id=) so that two deployments
            // sharing the same productName are correctly distinguished. Fall back to
            // the cookie only when the URL carries no id (e.g. deep-links).
            const resolvedProductId = selectedProductId || Cookies.get('Product Selected');
            const matchedById = resolvedProductId
                ? applicationsList.find((p) => String(p.id) === resolvedProductId)
                : null;
            const idMatchIsForThisRoute = matchedById &&
                (matchedById?.details?.productName?.toLowerCase() ===
                    routeProductLower ||
                    matchedById?.name?.toLowerCase() === routeProductLower);
            const matchedApp = (idMatchIsForThisRoute ? matchedById : null) ??
                applicationsList.find((p) => p?.details?.productName?.toLowerCase() ===
                    routeProductLower ||
                    p?.name?.toLowerCase() === routeProductLower) ??
                null;
            if (matchedApp) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { image_base64, ...detailsWithoutImage } = matchedApp.details || {};
                sessionStorage.setItem('current_application', JSON.stringify({
                    ...matchedApp,
                    details: detailsWithoutImage
                }));
                globalThis.dispatchEvent(new CustomEvent('current_application_changed'));
            }
            if (cancelled)
                return;
            if (!product) {
                setErrorMessage('The requested page could not be found.');
                return;
            }
            // Resolve URL directly from the matched app — never from stale sessionStorage.
            const baseUrl = matchedApp?.details?.productUrl ?? getBaseUrlForProduct(product) ?? null;
            if (!baseUrl) {
                console.error(`No baseUrl found for product "${product}"`);
                setErrorMessage(`"${product}" is not a recognized application. Please check the URL and try again.`);
                return;
            }
            const entryUrl = `${baseUrl}/remoteEntry.js`;
            const scope = `${product}App`;
            try {
                await loadComponentAsync(scope, product, entryUrl, (lazyComp) => {
                    if (cancelled)
                        return;
                    setRemoteComponent(lazyComp);
                });
            }
            catch (err) {
                if (cancelled)
                    return;
                const errMsg = err instanceof Error ? err.message : String(err);
                console.error(`Error loading ${scope}:`, err);
                AuthorizationAPI.logError({
                    msg: `${scope}: ${errMsg}`
                }).catch((error_) => console.error('[Product] logError failed:', error_));
                setErrorMessage(`Failed to load "${product}". Please try again or contact support.`);
            }
        };
        load();
        return () => {
            cancelled = true;
            // Tear down the bundle when the user navigates to /apps or logs out
            // so the next click always starts with a clean slate.
            if (product)
                cleanupScope(`${product}App`);
        };
    }, [product, applicationsList, selectedProductId]);
    if (errorMessage)
        return _jsx(ErrorPage, { message: errorMessage });
    return remoteComponent ? (_jsx(RemoteAppWrapper, { appName: product, enableStyleScoping: true, children: _jsx(Suspense, { fallback: _jsx(SkeletonLoader, {}), children: React.createElement(remoteComponent, {
                currentPath: location.pathname,
                search: location.search,
                hash: location.hash
            }) }) })) : (_jsx(SkeletonLoader, {}));
};
export default authenticatedRoute(Product);
